import { memo, useEffect, useRef, useState } from 'react';

export type AEMContentProviderProps = {
  src: string;
  style?: React.CSSProperties;
  logError?: ({ error, data, tags }: { error: Error; data?: any; tags?: string[] }) => void;
  onLoad?: () => void;
  onError?: () => void;
  id?: string;
  htmlContent?: string;
};

// Global registry to track which AEM scripts have already been executed
// to prevent duplicate global variable declarations
const executedScripts = new Set<string>();

const AEMContentProviderBase = (props: AEMContentProviderProps) => {
  const { src, logError, onLoad, onError, id, htmlContent, style } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    //for if the aemComponent has already been fetched
    if (htmlContent) {
      setContent(htmlContent);
      return;
    }

    fetchAEMComponent();

    function getContent(res: Response): Promise<string> {
      if (res.status === 200) {
        return res.text();
      } else {
        const errorMessage = `AEM Error Status Code ${res.status}`;
        setError(errorMessage);
        if (logError) logError({ error: new Error(errorMessage), tags: ['AEM'] });
        if (onError) onError();
        return Promise.resolve('');
      }
    }

    function fetchAEMComponent(): void {
      //for fetching aem components in components
      fetch(src)
        .then(getContent)
        .then(setContent)
        .catch((error) => {
          setError(error);
          if (logError) logError({ error, tags: ['AEM'] });
          if (onError) onError();
        })
        .finally(() => {
          if (onLoad) onLoad();
        });
    }

    function setContent(htmlContent: string): void {
      const slot = divRef?.current;
      if (!htmlContent || !slot) {
        return;
      }
      // If the slot already contains content, skip re-injection to
      // prevent duplicate <script> variable declarations (e.g. CXONE_ACCOUNT_ID)
      // that would throw a SyntaxError when scripts are re-evaluated.
      if (slot.innerHTML.trim() !== '') {
        return;
      }

      // Create a temporary container to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      // Extract and handle script tags separately to prevent duplicate declarations
      const scripts = Array.from(tempDiv.querySelectorAll('script'));
      const scriptsToExecute: HTMLScriptElement[] = [];

      scripts.forEach((script) => {
        const scriptContent = script.textContent || '';

        // Create a unique hash/key for this script to track if it's been executed
        const scriptKey = scriptContent.trim() || script.src || '';

        // Check if this exact script has already been executed globally
        if (scriptKey && executedScripts.has(scriptKey)) {
          // This script was already executed by another AEM fragment, skip it
          script.remove();
        } else {
          // Replace inline script with a placeholder to prevent auto-execution
          const placeholder = document.createComment('script-placeholder');
          script.replaceWith(placeholder);
          scriptsToExecute.push(script);
          // Mark this script as executed
          if (scriptKey) {
            executedScripts.add(scriptKey);
          }
        }
      });

      // Append the HTML content (without executable scripts)
      slot.innerHTML = tempDiv.innerHTML;

      // Manually execute scripts that weren't filtered out
      scriptsToExecute.forEach((originalScript) => {
        const newScript = document.createElement('script');
        if (originalScript.src) {
          newScript.src = originalScript.src;
        }
        if (originalScript.textContent) {
          newScript.textContent = originalScript.textContent;
        }
        // Copy attributes
        Array.from(originalScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        slot.appendChild(newScript);
      });
    }
  }, [src, onLoad, htmlContent, logError]);

  if (error) return null;

  return <div style={style} data-cy={id} ref={divRef} id={id}></div>;
};

export const AEMContentProvider = memo(AEMContentProviderBase);
